  import React, { useEffect, useRef, useState } from "react";
  import { Link, useLocation, useNavigate } from "react-router-dom";
  import { Menu as PrimeMenu } from "primereact/menu";
  import { Button } from "primereact/button";
  import { useSelector, useDispatch } from "react-redux";
  import { AppDispatch, RootState } from "../../redux/store";
  import { logout, switchCustomerMode } from "../../redux/slices/authSlice";
  import { logMessage } from "../../utils/logMessage";
  import { Avatar } from "primereact/avatar";
  import { Skeleton } from "primereact/skeleton";
  import { fetchBalance } from "../../redux/slices/accountSlice";
  import { Toast } from "primereact/toast";
  import { Dialog } from "primereact/dialog"; 
  import { NotificationButton } from "../features/NotificationButton";

  interface NavBarProps {
    isExpanded: boolean;
    setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  }

  const NavBar: React.FC<NavBarProps> = ({ isExpanded, setIsExpanded }) => {
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const toastRef = useRef<Toast>(null);
    const menu = useRef<PrimeMenu>(null);

    const { currentUser, loading } = useSelector((state: RootState) => state.auth);
    const { balance, isLoading } = useSelector((state: RootState) => state.account);
    
    const [showBalance, setShowBalance] = useState(false);
    
    const [isProdMode, setIsProdMode] = useState<boolean>(currentUser?.customer?.mode === "PRODUCTION");
    const [showModeConfirmation, setShowModeConfirmation] = useState(false);

    const getTitle = () => {
      const titles: { [key: string]: string } = {
        "/dashboard/settings": "Settings",
        "/dashboard/settings/profile": "Profile",
        "/dashboard/settings/profile/change-password": "Manage Password",
        "/dashboard/services": "Services",
        "/dashboard/developers": "Developers",
        "/admin/partners": "Manage Partners",
        "/admin/withdrawals": "Manage Withdrawals",
        "/admin/customers": "Manage Customers",
        "/admin/manage-document": "Manage Customers Documents",
        "/admin/manage-monetary-zone": "Manage Monetary Zones",
        "/admin/countries-monetary-zone": "Manage Countries",
        "/admin/manage-service": "Manage Services",
        "/admin/manage-ip-address": "Manage IP Address",
        "/dashboard/history": "Transactions History",
        "/admin/transaction-history": "Transactions History",
        "/admin/manage-roles": "Manage Roles",
        "/dashboard/payment-link": "Manage Payment Link",
        "/admin/payment-link": "Payment Links List",
        "/admin/manage-withdrawals": "Manage withdrawals",
        "/dashboard/withdrawal-list": "withdrawal history",
        "/dashboard/manage-webhook": "Manage webhook",
      };
      return titles[location.pathname] || "Dashboard";
    };

    const handleLogout = () => {
      dispatch(logout())
        .unwrap()
        .then(() => {
          navigate("/auth");
          localStorage.removeItem("otpConfirmed");
        })
        .catch((error) => {
          logMessage("Logout failed:", error);
        });
    };

    const profileItems = [
      { label: "Profile Details", icon: "pi pi-user", command: () => navigate("/dashboard/settings") },
      { label: "Logout", icon: "pi pi-sign-out", command: handleLogout },
    ];

    const handleToggleClick = () => {
      setShowModeConfirmation(true);
    };

    const handleConfirmModeChange = async () => {
      setShowModeConfirmation(false);
      try {
        const result = await dispatch(switchCustomerMode()).unwrap();
        setIsProdMode(!isProdMode);
        
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Mode changed successfully",
          life: 3000,
        });
        
        dispatch(fetchBalance());
        
      } catch (error: any) {
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: error.message || "Failed to switch mode",
          life: 3000,
        });
      }
    };

    const handleCancelModeChange = () => {
      setShowModeConfirmation(false);
    };

    useEffect(() => {
      if (currentUser?.role.role_code === "CUSTOMER"){
        dispatch(fetchBalance());
      }
    }, [dispatch]);

    const confirmationFooter = (
      <div>
        <Button label="Cancel" icon="pi pi-times" onClick={handleCancelModeChange} className="p-button-text" />
        <Button label="Confirm" icon="pi pi-check" onClick={handleConfirmModeChange} autoFocus />
      </div>
    );

    return (
      <header className="z-100 flex justify-between shadow-sm items-center bg-white p-7 py-4 h-20 border-b">
        
        <div className="flex items-center">
          <Button
            icon={isExpanded ? "pi pi-align-left" : "pi pi-align-right"}
            onClick={() => setIsExpanded(!isExpanded)}
            className="hidden md:block text-black p-0 border-none focus:outline-none focus:shadow-none"
          />

          <h1 className="hidden md:block text-md text-secondary-500">{getTitle()}</h1>


          {!loading && currentUser?.role.role_code === "CUSTOMER" && (
            <div className="p-4">
              <label className="inline-flex items-center cursor-pointer">
                <span className="text-secondary-500 mr-3 text-sm font-medium">Mode</span>
                <input 
                  type="checkbox" 
                  className="sr-only" 
                  checked={isProdMode} 
                  onChange={handleToggleClick} 
                />
                <div className={`w-11 h-6 rounded-full relative transition-colors ${isProdMode ? "bg-green-500" : "bg-red-500"}`}>
                  <div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform ${isProdMode ? "translate-x-5" : ""}`} />
                </div>
                <span className="text-secondary-500 mx-1">{isProdMode ? "Production" : "Test"}</span>
              </label>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {!loading && currentUser?.role.role_code === "CUSTOMER" && (
            <div className="hidden md:flex items-center gap-2">
              <span className="text-secondary-500">Balance :</span>
              {isLoading ? (
                <Skeleton style={{ width: "100px", fontSize: "1rem" }} />
              ) : (
                <h5 className="cursor-pointer text-primary-500">
                  {showBalance ? isNaN(Number(balance)) ? "0.00" : Number(balance).toFixed(2) : "******"}</h5>
              )}
              <span className="cursor-pointer text-primary-500" onClick={() => setShowBalance(!showBalance)}>
                {showBalance ? <i className="pi pi-eye-slash"></i> : <i className="pi pi-eye"></i>}
              </span>
            </div>
          )}
          <div className="hidden md:block">
           <NotificationButton/>
          </div>
        
          <div className="relative items-center ">

            <Button
              label={`${currentUser?.firstname} ${currentUser?.lastname}`}
              icon="pi pi-angle-down"
              className="p-button-text text-secondary-500 flex items-center"
              onClick={(e) => menu.current?.toggle(e)}
            >
              <Avatar label={currentUser?.firstname?.[0]?.toUpperCase() || "V"} size="normal" className="text-secondary-500 w-8 h-8 ml-2" shape="circle" />
            </Button>
            <PrimeMenu model={profileItems} popup ref={menu} />
          </div>
        </div>
        
        <Dialog 
          visible={showModeConfirmation}
          onHide={handleCancelModeChange}
          header="Confirm Mode"
          footer={confirmationFooter}
          style={{ width: '40vw' }}
        >
          <div className="confirmation-content flex items-center">
            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
            <span>
              Are you sure you want to switch to <strong>{isProdMode ? "Test" : "Production"}  mode ?</strong>
              {isProdMode ? " Your production transactions will be disabled." : " You will be able to perform real transactions."}
            </span>
          </div>
        </Dialog>
        
        <Toast ref={toastRef} />
      </header>
    );
  };

  export default NavBar;